import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@mui/styles';
import {
  Box,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import API from 'common/api';
import { PlaceHolderText } from 'components/PlaceholderCard/PlaceholderCard';
import Progress from 'components/Progress/Progress';
import useList from 'hooks/useList';
import TablePaginator from 'components/TablePaginator/TablePaginator';
import useUser from 'hooks/useUser';
import { Card, Typography, RouterLink } from 'bso-ui';
import PAGES from 'common/pages';
import UserToolbar from './UserToolbar';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2)
  },
  inner: {
    minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  }
}));

const UsersList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();
  const height = '80vh';
  const [{ data: userData }] = useUser();
  const query = {
    storeKey: 'user-list',
    apiEndpoint: API.USER,
  };
  const [{
    data, isLoading, isError, refetch, isFetched, isFetching
  }] = useList(query.storeKey, query.apiEndpoint);

  if (isError || (isFetched && !data)) {
    return (
      <div style={{ height }}>
        <PlaceHolderText variant="h3" content="Error getting users" height="100%" />
      </div>
    );
  }

  const renderData = () => (
    <Card
      id="UsersListCard"
      className={clsx(classes.root)}
    >
      <CardContent className={classes.content}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
        </div>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle1">Username</Typography></TableCell>
                  <TableCell><Typography variant="subtitle1">First name</Typography></TableCell>
                  <TableCell><Typography variant="subtitle1">Last name</Typography></TableCell>
                  <TableCell><Typography variant="subtitle1">E-mail</Typography></TableCell>
                </TableRow>
              </TableHead>
              {data && data.results.length > 0
                ? (
                  <TableBody>
                    {data.results.map((user) => (
                      <TableRow
                        className={classes.tableRow}
                        hover
                        key={user.id}
                        onDoubleClick={() => {
                          navigate(`${PAGES.USER_MGMT}/${user.id}${location.search}`);
                        }}
                      >
                        <TableCell>
                          <RouterLink
                            disableUnderline
                            to={{
                              pathname: `${PAGES.USER_MGMT}/${user.id}`,
                              search: location.search
                            }}
                          >
                            <Typography color="primary" variant="body2" component="span">{user.username}</Typography>
                          </RouterLink>
                        </TableCell>
                        <TableCell>
                          <Typography color="primary" variant="body2">{user.first_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color="primary" variant="body2">{user.last_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color="primary" variant="body2">{user.email}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )
                : null}
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePaginator
          count={data.count}
          previous={data.previous}
          next={data.next}
          isFetched={isFetched}
        />
      </CardActions>
    </Card>
  );

  return (
    <Box p={3}>
      <UserToolbar title="Users" isFetching={isFetching} refetch={refetch} data={data} userData={userData} />
      {!isLoading
        ? renderData()
        : <Progress />}
    </Box>
  );
};

export default UsersList;
